import {
  backdropAlert,
  caseInsensitiveSort,
  getUser,
  localDateTimeString,
  LOGO_MAPPING,
  LOGO_MAPPING_COLORS,
  numericDateString,
  showToast,
  titleCase,
  uuidV4,
  wrappedGet,
  wrapTooltip,
} from "./general/utils.js";
import { TAGS } from "./shared/tags.js";
import { setCurrentCandidate } from "./state.js";
import { todosModalItemStore } from "../../../svelte/src/modals/TodosModal/stores.js";

let NOT_INTERESTED_REASONS = {};
const showReplyTags = getUser().automateReplyTags === true;

const STAGE_COLORS = {
  sourced: "#1da2d8",
  sent: "#2474CB",
  opened: "#2190B5",
  clicked: "#2190B5",
  replied: "#1EADA0",
  interested: "#1BC98A",
  notInterested: "#5a5c69",
  booked: "#625df5",
};

export const stageTitle = (stage, opts = {}) => {
  const mapping = { sent: "Messaged", booked: "Meeting" };
  const ret = mapping[stage] || titleCase(stage);

  const candidateId = opts.candidateId || null;
  const color = STAGE_COLORS[stage];

  return opts.html
    ? `
        <div class="dropdown ${opts.disabled ? "disabled" : ""}">
          <span type="button" class="badge d-inline-flex align-items-center" data-toggle="dropdown" style="color: white; user-select: none; background-color: ${color}">${ret}<i class="fa-solid fa-caret-down ml-1" style="color:white"></i></span>
          <div class="dropdown-menu" style="width:fit-content !important">
            <a class="dropdown-item" id="sourcedDropdown" data-candidateId="${candidateId}"><i class="fa-solid fa-search fa-fw mr-2"></i> Sourced</a>
            <a class="dropdown-item" id="sentDropdown" data-candidateId="${candidateId}"><i class="fa-solid fa-share fa-fw mr-2"></i> Messaged</a>
            <a class="dropdown-item" id="openedDropdown" data-candidateId="${candidateId}"><i class="fa-solid fa-envelope-open fa-fw mr-2"></i> Opened</a>
            <a class="dropdown-item" id="repliedDropdown" data-candidateId="${candidateId}"><i class="fa-solid fa-reply fa-fw mr-2"></i> Replied</a>
            <a class="dropdown-item" id="interestedDropdown" data-candidateId="${candidateId}"><i class="fa-solid fa-grin-stars fa-fw mr-2"></i> Interested</a>
            <a class="dropdown-item" id="notInterestedDropdown" data-candidateId="${candidateId}"><i class="fa-solid fa-face-expressionless fa-fw mr-2"></i> Not Interested</a>
            <a class="dropdown-item" id="bookedDropdown" data-candidateId="${candidateId}"><i class="fa-solid fa-calendar fa-fw mr-2"></i> Meeting</a>
          </div>
        </div>`
    : ret;
};

export const locationLink = (location) =>
  location
    ? `<a href="https://google.com/maps/search/${location}" target="_blank">${location}</a>`
    : "";

export const companyLink = (company) =>
  company
    ? `<a href="https://linkedin.com/search/results/companies/?keywords=${company}" target="_blank" rel="noreferrer noopener">${company}</a>`
    : "";

const candidateDownloadRow = (candidate) => ({
  Campaign: candidate.campaign,
  Sourcer: (candidate.sourcer || {}).name,
  Sourced: numericDateString(candidate.createdAt),
  Source: candidate.sourceName || candidate.source,
  Stage: stageTitle(candidate.stage),
  Opens: candidate.opens,
  Clicks: candidate.clicks,
  "Full Name": candidate.name || candidate.firstName,
  "First Name": candidate.firstName,
  "Last Name": candidate.lastName,
  School: candidate.school,
  Salutation: candidate.salutation,
  Emails: (candidate.emails || []).join(", "),
  "Phone Numbers": (candidate.phones || []).join(", "),
  Company: candidate.company,
  "Email Domains": (candidate.domains || []).join(", "),
  "Role / Job Title": candidate.role,
  Location: candidate.location,
  "Location (City)": candidate.city,
  "Location (State)": candidate.state,
  "Location (Country)": candidate.country,
  Tags: (candidate.tagIds || [])
    .reduce((tagRet, tagId) => {
      const tag = (TAGS || []).find((x) => x.tagId === tagId);
      if (tag) tagRet.push(tag.title);
      return tagRet;
    }, [])
    .sort(),
  Skills: (candidate.skills || []).join(", "),
  "Company LinkedIn URL": candidate.companyLinkedinUrl,
  "GitHub Repo": candidate.repo,
  "Custom Variable": candidate.customVariable,
  "Custom Variable 2": candidate.customVariable2,
  "Custom Variable 3": candidate.customVariable3,
  "Custom Variable 4": candidate.customVariable4,
  Comments: candidate.comments,
  "AngelList URL": candidate.angelListUrl,
  "Crunchbase URL": candidate.crunchbaseUrl,
  "Dribbble URL": candidate.dribbbleUrl,
  "Facebook URL": candidate.facebookUrl,
  "LinkedIn URL": candidate.linkedinUrl,
  "LinkedIn Sales Navigator URL": candidate.liSalesnavUrl,
  "LinkedIn Recruiter URL": candidate.liRecruiterCorporateUrl,
  "GitHub URL": candidate.githubUrl,
  "Kaggle URL": candidate.kaggleUrl,
  "Meetup URL": candidate.meetupUrl,
  "Quora URL": candidate.quoraUrl,
  "StackOverflow URL": candidate.stackoverflowUrl,
  "Twitter URL": candidate.twitterUrl,
  "Xing URL": candidate.xingUrl,
  "Acclaim URL": candidate.acclaimUrl,
});

export const downloadCandidates = (candidates) => {
  const headers = new Set();
  const data = candidates.reduce((ret, x) => {
    const item = {};
    Object.entries(candidateDownloadRow(x)).forEach(([key, val]) => {
      if (!val) return;
      item[key] = val;
      headers.add(key);
    });
    ret.push(item);
    return ret;
  }, []);

  if (data.length === 0) {
    backdropAlert("Please select some contacts");
    return;
  }

  const columns = caseInsensitiveSort(Array.from(headers));

  const csv = Papa.unparse(data, { columns });
  // \uFEFF is the BOM (byte order mark) - this is required to make Excel
  // recognize the encoding, but other programs may display the BOM
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  window.saveAs(blob, "Contacts.csv");
};

export const socialLinksToIcons = (socialLinks) => {
  const links = socialLinks.map((link) => {
    const key = Object.keys(LOGO_MAPPING).find((k) => link.includes(k));
    const colorKey = Object.keys(LOGO_MAPPING_COLORS).find((k) =>
      link.includes(k),
    );

    const icon = key ? LOGO_MAPPING[key] : "fas fa-link";
    const color = colorKey ? LOGO_MAPPING_COLORS[colorKey] : "";

    return `
        <a href="${link}" style="color: ${color}" target="_blank" rel="noreferrer noopener">
          <i class="${icon} mx-2"></i>
        </a>`;
  });
  return links.join("");
};

export const trackingCounter = (count, kind, candidateId, candidateName) => `
    <span
      class="font-weight-bold text-primary tracking-icon"
      style="cursor: pointer;"
      data-kind="${kind}"
      data-candidateId="${candidateId}"
      data-count="${count}"
      data-candidateName="${candidateName}"
      >${count > 10 ? "10+" : count}
    </span>`;

// For outreach.html
export const addActivityTab = (activity) => {
  if (activity.hidden) return;

  const id = uuidV4();

  const isExpandable = activity.subject || activity.content;

  const toEmail = activity.to
    ? `<span class='px-2 font-weight-bold'>To</span><span>${activity.to}</span><br>`
    : "";
  const fromEmail = activity.from
    ? `<span class='px-2 font-weight-bold'>From</span><span>${activity.from}</span><br>`
    : "";
  const subject = activity.subject
    ? `<span class='px-2 font-weight-bold'>Subject</span><span>${activity.subject}</span><br>`
    : "";
  const content = activity.content
    ? `<div class="px-2 py-1 mt-2" style="white-space: break-spaces;">${activity.content}</div>`
    : "";

  const isNotInterested = activity.name === "Not Interested";
  const reasonObj = activity.notInterestedReason;
  const reasonExists = reasonObj && Object.keys(reasonObj).length > 0;

  const notInterestedString =
    reasonExists && showReplyTags
      ? `<span id="${id}-reason-string">(${reasonObj.title})</span>`
      : "";
  const notInterestedBadge =
    isNotInterested && showReplyTags
      ? `<div id="${id}-reason-badge" class="badge badge-dark m-2 not-interested-dropdown dropdown" style="text-transform: capitalize">
          <select data-id="${activity["message-id"]}" id="${id}-reason-selector" class="selectpicker" placeholder='Not interested'></select>
        </div>`
      : "";

  let user = "";

  if (activity.user) {
    user = wrapTooltip(
      `<img class="mx-4 rounded-circle user-tooltip" src="${activity.user.picture}" style="width:1.5rem;height:1.5rem" />`,
      activity.user.name,
    );
  }

  $("#activitiesDiv").append(/* html */ `
        <div class='border rounded p-1 mb-2 ${
          isExpandable ? "expandable-record" : ""
        }' >
            <div class="row mx-0 py-1 px-2 d-flex align-items-center" style="min-height: 2rem" id="${id}-toggler">
                <div class="col-6 p-0 m-0 d-flex align-items-center" style="font-weight: bold;">
                    <i style="color: ${activity.color};" class="${
                      activity.icon
                    } mr-2"></i>
                    <span style="line-height: 100%;">${
                      activity.name
                    } ${notInterestedString}</span>
                    ${
                      isExpandable
                        ? '<i class="far fa-caret-down todo-caret mx-2"></i>'
                        : ""
                    }
                </div>
                <div class="col-1 p-0 ml-0 d-flex align-items-center justify-content-center">${user}</div>
                <div class="col-5 p-0 m-0 d-flex align-items-center justify-content-end">
                    <span class="ml-4 text-right" style="line-height: 100%;">${
                      activity.timestamp && !activity.scheduled
                        ? localDateTimeString(activity.timestamp)
                        : ""
                    }</span>
                </div>
            </div>
            <div id='${id}' style='display: none;'>
                <hr class='mx-2 mt-1 mb-2' />
                ${toEmail}
                ${fromEmail}
                ${subject}
                ${content}
                ${notInterestedBadge}
            </div>
        </div>`);

  if (isExpandable) $(`#${id}-toggler`).click(() => $(`#${id}`).toggle());

  if (isNotInterested && showReplyTags) {
    const val = reasonExists ? reasonObj.notInterestedTagId : undefined;
    const selector = $(`#${id}-reason-selector`);
    selector.find("option").remove();

    if (!reasonExists) {
      selector.append(
        $("<option>", {
          value: undefined,
          text: "Not interested",
          selected: true,
          disabled: true,
        }),
      );
    }

    Object.entries(NOT_INTERESTED_REASONS).forEach(([tagId, title]) => {
      const opts = {
        value: tagId,
        text: `Not Interested: ${title}`,
        "data-string": `(${title})`,
        selected: tagId === val,
      };
      selector.append($("<option>", opts));
    });
    selector.selectpicker("refresh");

    selector.change((e) => {
      const selected = selector.selectpicker("val");
      if (!selected) return;
      const string = $(e.target).find("option:selected").attr("data-string");
      const identifier = activity["message-id"] ? "message-id" : "inmailHash";
      const data = {
        identifier,
        [identifier]: activity[identifier],
        notInterestedTagId: selected,
      };
      $.post({
        url: "/api/activities/modify_reply_tag",
        data: JSON.stringify(data),
        success: () => {
          showToast({
            title: "Reason successfully changed",
            type: "success",
          });
          $(`#${id}-reason-string`).text(string);
        },
      });
    });
  }
};

export const displayActivity = (activity, html = false) => {
  if (activity.hidden) return "";
  if (html) {
    const time = activity.afterManualStep
      ? "after manual step"
      : localDateTimeString(activity.timestamp);
    const replyIcon =
      activity.isReply || activity.isInmailReply ? "reply-icon" : "";
    const timeTitle = activity.scheduled ? "" : `(${time})`;
    const identifier = activity["message-id"] ? "message-id" : "inmailHash";
    const dataAttr = identifier === "message-id" ? "messageId" : "inmailHash";
    return wrapTooltip(
      ` <i class="${activity.icon} px-1 activity-tooltip ${replyIcon}"
            data-identifier="${identifier}"
            data-${dataAttr}="${activity[identifier] || ""}"
            data-search="${activity.name.toLowerCase()}">
          </i>`,
      `${activity.name}<br>${timeTitle}`,
    );
  }
  return activity.name;
};

let index;

let selectedCandidateData = {};

// New contact modal will go through this function
export const handleLeftClick = (e, nav, selector, candidate = undefined) => {
  let tableData = [];
  let numberOfContacts = 0;
  if (selector) {
    tableData = $(`#${selector}`).DataTable().rows().data().toArray();
    numberOfContacts = tableData.length;
  }

  if (e) {
    const tr = $(e.target).closest("tr");
    const candidateIdSpan = $(tr).find(".candidate-id-span");
    const candidateId = $(candidateIdSpan).attr("data-candidateId");
    if (selector === "callsTable" && candidateId === "undefined") return;
    tableData.some((c, ind) => {
      if (c.candidateId === candidateId) {
        index = ind;
        return true;
      }
      return false;
    });
  }

  index = (index + (nav || 0) + numberOfContacts) % numberOfContacts;

  if (selector === "candidatesTable") {
    selectedCandidateData = tableData[index];
  } else if (selector === "callsTable") {
    const currentIndex = index;
    do {
      selectedCandidateData = tableData[index].candidate;
      if (selectedCandidateData.candidateId) break;
      index = (index + (nav || 1) + numberOfContacts) % numberOfContacts;
    } while (index !== currentIndex);
    if (!selectedCandidateData.candidateId) return;
  } else if (candidate !== undefined) {
    selectedCandidateData = candidate;
  } else {
    return;
  }

  setCurrentCandidate(selectedCandidateData);

  wrappedGet({
    url: "/api/not_interested/get_all",
    success: (res) => {
      NOT_INTERESTED_REASONS = {};
      res.reserved.concat(res.custom).forEach((reason) => {
        NOT_INTERESTED_REASONS[reason.notInterestedTagId] = reason.title;
      });
    },
  });

  // Don't display arrows if there's only one contact
  const navigationFunction = (i) => {
    if (numberOfContacts === 1) return null;
    return () => handleLeftClick("", i, selector);
  };

  // +1 to go next, -1 to go back
  const next = navigationFunction(1);
  const prev = navigationFunction(-1);

  todosModalItemStore.set({
    ...selectedCandidateData,
    toDoId: null, // Missing toDoId tells the modal to display the contact version only
    next,
    prev,
  });
};
